import { DateTime } from 'luxon'
import type { ReactElement } from 'react'

import { SERIES_COMMENT_PAGE_SIZE } from '@/common/services/seriesPage/constants'
import type { SeriesPageData } from '@/common/services/seriesPage/types'
import parseQueryNumber from '@/utils/parseQueryNumber'
import { makeServerProps } from '@/server/query'
import getSeriesPageData from '@/server/services/seriesPage/getSeriesPageData'

import makePage from '@/client/services/pages/makePage'
import { PROGRAM_WORLD_TO_PROGRAM_SLUG } from '@/client/services/programs/constants'
import * as url from '@/client/services/url'
import { isProgramWorld } from '@/client/services/worlds'

import SeriesHome from '@/components/Series/SeriesHome/SeriesHome'
import SeriesLayout from '@/components/Series/SeriesLayout/SeriesLayout'
import { SeriesTab } from '@/components/Series/SeriesLayout/types'

function Series({ seriesPageData }: { seriesPageData: SeriesPageData | null }): ReactElement {
  return (
    <SeriesLayout
      seriesPageData={seriesPageData}
      currentTab={SeriesTab.LEARN}
      title={seriesTitle => seriesTitle}
      description={({ tutorDisplayName, title, startDate }) =>
        `${
          tutorDisplayName
        } is hosting a series titled "${title}" on Schoolhouse.world, a free online tutoring platform. Check it out on ${startDate
          .toUTC()
          .toLocaleString(DateTime.DATETIME_FULL)}.`
      }
      headerOptions={{
        heading: seriesTitle => seriesTitle,
        breadcrumbs: ({ world }) =>
          world != null && world.title !== ''
            ? [
                {
                  label: world.title,
                  url: isProgramWorld(world.slug)
                    ? url.getDashboardProgramsUrl({
                        programSlug: PROGRAM_WORLD_TO_PROGRAM_SLUG[world.slug],
                      })
                    : url.getDashboardSessionsUrl({ worldSlug: world.slug }),
                },
              ]
            : [],
      }}
    >
      {seriesPageData != null ? <SeriesHome series={seriesPageData} /> : null}
    </SeriesLayout>
  )
}

const SeriesPage = makePage(Series, {
  onboardingUsers: 'allowed',
})

export const getServerSideProps = makeServerProps(SeriesPage, {
  load: (user, ctx) =>
    getSeriesPageData(parseQueryNumber({ value: ctx.query.id, defaultValue: 0 }), user, {
      commentLimit: SERIES_COMMENT_PAGE_SIZE,
    }),
  extraProps: (_user, _ctx, loaded) => {
    if (loaded == null) {
      return { seriesPageData: null }
    }
    const { commentsPage: _commentsPage, ...seriesPageData } = loaded
    return { seriesPageData }
  },
  hydrate: ({ trpc, queryClient, loaded }) => {
    if (loaded == null) {
      return
    }
    queryClient.setQueryData(
      trpc.series.comments.infiniteQueryOptions(
        { seriesId: loaded.id, limit: SERIES_COMMENT_PAGE_SIZE },
        { getNextPageParam: page => page.nextCursor, initialCursor: null }
      ).queryKey,
      { pages: [loaded.commentsPage], pageParams: [null] }
    )
  },
})
export default SeriesPage
